import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { SQLModelForStepsHelper } from 'wizards/helpers/sql-model-helpers/sql-model-for-steps-helper';
import { SQLPlanFromStepsHelper } from 'wizards/helpers/sql-model-helpers/sql-plan-from-steps-helper';
import { SQLFormModel } from 'wizards/models/SQL/sql-form-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';
import { AdvancedOptionsStepComponent } from 'wizards/steps/advanced-options-step/advanced-options-step.component';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { RetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/retention-policy-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SimpleScheduleStepComponent } from 'wizards/steps/simple-schedule-step/simple-schedule-step.component';
import { SQLServerInstanceStepComponent } from 'wizards/steps/sql-server-instance-step/sql-server-instance-step.component';
import { SQLSourceDatabasesStepComponent } from 'wizards/steps/sql-source-databases-step/sql-source-databases-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { WizardBaseClass } from '../wizard-base.class';

type SQLWizardHelpDocumentationLinks =
  | 'welcome'
  | 'storage'
  | 'server'
  | 'data'
  | 'compressionEncryption'
  | 'retention'
  | 'schedule'
  | 'prePost'
  | 'chain'
  | 'log';

@UntilDestroy()
@Component({
  selector: 'mbs-sql-wizard',
  templateUrl: './sql-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => SQLWizardComponent)
    }
  ],
  imports: [
    AdvancedOptionsStepComponent,
    AsyncPipe,
    CommonModule,
    CompressionEncryptionStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RetentionPolicyStepComponent,
    SQLServerInstanceStepComponent,
    SQLSourceDatabasesStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    SimpleScheduleStepComponent
  ]
})
export class SQLWizardComponent extends WizardBaseClass {
  i18nPipe: I18NextPipe;

  public serverInstanceTitle = this.i18nPipe.transform('wizards:sql:sqlServerInstanceStepMenuTitle');
  public helpLink: WizardHelpLink<SQLWizardHelpDocumentationLinks>;

  protected get needSubscribeThroughPipe(): boolean {
    return !this.isRDMode || this.isEdit;
  }

  constructor() {
    const i18nPipe = inject(I18NextPipe);
    const services = inject(WizardsServicesHubService);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new SQLFormModel(this.isNBF, this.isUnix);

    this.initSubscribes();
  }

  updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) {
      // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
      const rightVersionForSchedule = this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 2) >= 80;
      if (!this.isEdit && this.isNBF && !this.isUnix && !rightVersionForSchedule) {
        this.FormModel.scheduleStep.ScheduleType = 'noschedule';
        this.FormModel.scheduleStep.fullBackupData.enabled = false;
      }

      return this.setDefaultValue();
    }

    this.FormModel = SQLModelForStepsHelper.SQLModelFromPlan(plan, this.defaultSettings());
  }

  protected getPlanForSaving(): any {
    return SQLPlanFromStepsHelper.getUpdatedPlan(this.loadedPlan.plan, this.FormModel, this.defaultSettings());
  }

  protected loadInfoHandlerFinally() {
    this.cdr.detectChanges();
  }

  getRAWHelpLinks(): Record<SQLWizardHelpDocumentationLinks, string> {
    return {
      welcome: `backup/mssql/console#planname`,
      storage: `backup/mssql/console#storage`,
      server: `backup/mssql/console#server`,
      data: `backup/mssql/console#data`,
      compressionEncryption: `backup/mssql/console#compression`,
      retention: `backup/mssql/console#retention`,
      schedule: `backup/mssql/console#schedule`,
      prePost: `backup/mssql/console#prepost`,
      chain: `backup/mssql/console#chain`,
      log: `backup/mssql/console#log`
    };
  }
}
